import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router';
import { useMsal } from '@azure/msal-react';
import type { AppUser } from '../data/types';
import { isAdmin } from '../data/scope';
import { SyncIndicator, useSyncStatus } from './SyncIndicator';
import { ActionButton, untilLeaving } from './ActionButton';
import { getPendingMove } from '../canonicalHost';
import { getSyncEngine, getTaskOutbox } from '../local';
import { useTaskOutbox } from './useTaskOutbox';
import { myDeviceAudits, removeFromDevice } from '../audit/lifecycle';
import { useAppUser } from '../auth/AppUserContext';
import { getActiveAccount } from '../auth/msal';
import { forgetCachedProfile, stopSavingProfiles } from '../auth/profileCache';
import { isAuditFormRoute } from './layoutRoutes';
import { apiMode } from '../api/mode';
import { clearReadCache } from '../graph/readCache';
import { roleLabel } from './roleLabel';

/** A staging build (VITE_APP_ENVIRONMENT=staging — a future separate staging app) — always labelled so nobody mistakes it for production. */
const STAGING = import.meta.env.VITE_APP_ENVIRONMENT === 'staging';
const BUILD_REF = import.meta.env.VITE_BUILD_REF ?? '';
/** "<branch> <7-char commit>" for the label — which work is live (the latest run wins). */
export const BUILD_SHORT = BUILD_REF.includes('@')
  ? `${BUILD_REF.slice(0, BUILD_REF.lastIndexOf('@')).replace(/^claude\//, '')} ${BUILD_REF.slice(BUILD_REF.lastIndexOf('@') + 1, BUILD_REF.lastIndexOf('@') + 8)}`
  : '';

export function Layout({ user, children }: { user: AppUser; children: ReactNode }) {
  const { instance } = useMsal();
  const { impersonating, realUser, setImpersonation } = useAppUser();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Phones: the navigation lives behind a ☰ button (owner ruling 2026-10-28,
  // UI review U1/U4 — two nav rows made a 165 px sticky header). Closes on
  // every page change, a tap on a link (even the page you're on), a tap
  // outside the header, and Escape — which puts focus back on ☰ (the link
  // or account button it was on is hidden with the menu).
  const [navOpen, setNavOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  useEffect(() => setNavOpen(false), [pathname]);
  useEffect(() => {
    if (!navOpen) return;
    const escape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setNavOpen(false);
      toggleRef.current?.focus();
    };
    const outside = (e: PointerEvent) => {
      if (headerRef.current && e.target instanceof Node && !headerRef.current.contains(e.target)) setNavOpen(false);
    };
    document.addEventListener('keydown', escape);
    document.addEventListener('pointerdown', outside);
    return () => {
      document.removeEventListener('keydown', escape);
      document.removeEventListener('pointerdown', outside);
    };
  }, [navOpen]);

  return (
    <div className="app-shell">
      <div className={`app-top${isAuditFormRoute(pathname) ? '' : ' app-top-pinned'}`}>
        <MovedBanner />
        {impersonating && (
          <div className="impersonation-banner">
            👁 Viewing as <strong>{user.displayName}</strong> ({roleLabel(user.role)}) — a
            testing view only; your access as {realUser?.displayName} is
            unchanged.
            <button
              className="btn btn-small"
              onClick={() => {
                setImpersonation(null);
                navigate('/');
              }}
            >
              Stop impersonating
            </button>
          </div>
        )}
        <header ref={headerRef} className={`app-header${navOpen ? ' nav-open' : ''}`}>
          {/* The logo + name go Home, like most apps (owner ask 2026-10-18). */}
          <Link className="app-title" to="/" aria-label="SSP AuditPlus — Home">
            <span className="app-logo" aria-hidden>
              ✓
            </span>
            SSP AuditPlus
          </Link>
          {/* Phones only (CSS): the sync status stays in the slim top row, then the menu button. */}
          <span className="phone-only-sync">
            <SyncIndicator />
          </span>
          <button
            ref={toggleRef}
            type="button"
            className="nav-toggle"
            aria-expanded={navOpen}
            aria-controls="app-menu"
            aria-label={navOpen ? 'Close the menu' : 'Open the menu'}
            onClick={() => setNavOpen((o) => !o)}
          >
            <span aria-hidden>{navOpen ? '✕' : '☰'}</span>
          </button>
          {/* Desktop: display: contents (the header row as before). Phones: the drop-down. */}
          <div
            className="app-menu"
            id="app-menu"
            onClick={(e) => {
              if (e.target instanceof Element && e.target.closest('a')) setNavOpen(false);
            }}
          >
          <nav className="app-nav">
            <NavLink to="/" end>
              Home
            </NavLink>
            {/* Everyone gets Audits: conduct roles start/resume, all roles
                (incl. StoreTeam/Executive) read completed audit records. */}
            <NavLink to="/audits">Audits</NavLink>
            {/* Executives read tasks too (resolution, proof, comments — owner ask 2026-10-21); read-only. */}
            <NavLink to="/tasks">Tasks</NavLink>
            {user.role !== 'StoreTeam' && <NavLink to="/analytics">Analytics</NavLink>}
            {/* Reports = the catalogue of structured reports; Budgets and Goals its own item (owner rulings 2026-10-25, docs/reports.md). */}
            {user.role !== 'StoreTeam' && <NavLink to="/reports">Reports</NavLink>}
            {user.role !== 'StoreTeam' && <NavLink to="/budgets">Budgets and Goals</NavLink>}
          </nav>
          <div className="app-user">
            {/* Admin area lives on the right of the header (his layout ask). */}
            {isAdmin(user.role) && (
              <nav className="app-nav" aria-label="Admin">
                <NavLink to="/admin">⚙ Admin</NavLink>
              </nav>
            )}
            <SyncIndicator />
            {STAGING && (
              <span
                className="sync-chip staging-chip"
                title={`STAGING copy of AuditPlus — work not yet merged. Build: ${BUILD_REF || 'unknown'}`}
              >
                🧪 Staging{BUILD_SHORT && ` · ${BUILD_SHORT}`}
              </span>
            )}
            {apiMode() === 'tab' && (
              <span
                className="sync-chip api-chip"
                title="Testing the secure server: this tab reads data through /api. Add ?api=0 to the address to switch back."
              >
                🔒 Server mode
              </span>
            )}
            <UserMenu user={user} viewingAs={impersonating ? (realUser?.displayName ?? null) : null} onSignOut={() => signOut((realUser ?? user).upn, getActiveAccount()?.username, () => untilLeaving(instance.logoutRedirect()))} />
          </div>
          </div>
        </header>
      </div>
      <main className="app-main">{children}</main>
    </div>
  );
}

/**
 * The name at the top right opens a small menu (owner ask 2026-10-28: a bell
 * reads as "pending alerts"; the person's own settings belong under their
 * name): My account (access, notifications, time zone, this device, app
 * details), My notifications (everyone but store teams) and Sign out. A disclosure (button + list), not an ARIA menu: Tab moves
 * through it; Escape, a click elsewhere or moving to another page closes it.
 */
export function UserMenu({ user, viewingAs, onSignOut }: { user: AppUser; viewingAs: string | null; onSignOut: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  // Signing out keeps the menu (and its busy Sign out) open until the page leaves —
  // a closed-and-reopened menu must never start a second sign-out (QA 2026-10-28).
  const [signingOut, setSigningOut] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();
  const close = (refocus = false) => {
    if (signingOut) return;
    setOpen(false);
    if (refocus) button.current?.focus();
  };
  useEffect(() => close(), [pathname]);
  useEffect(() => {
    if (!open) return;
    const outside = (e: PointerEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) close();
    };
    // Only while focus is in the menu: an Escape meant for a dialog on top is the dialog's.
    const escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && box.current?.contains(document.activeElement)) close(true);
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }); // re-bound each render so `close` sees the current signingOut
  return (
    <div
      className="user-menu"
      ref={box}
      onBlur={(e) => {
        // Tabbing out of the menu closes it.
        if (open && e.relatedTarget instanceof Node && !e.currentTarget.contains(e.relatedTarget)) close();
      }}
    >
      <button
        ref={button}
        type="button"
        className="user-menu-button"
        aria-expanded={open}
        aria-controls={open ? 'user-menu-list' : undefined}
        aria-label={`${user.displayName} (${roleLabel(user.role)}) — account menu`}
        onClick={(e) => {
          e.currentTarget.focus(); // Safari doesn't focus a clicked button — Escape needs focus inside
          if (open) close();
          else setOpen(true);
        }}
      >
        <span className="user-menu-icon" aria-hidden>
          👤
        </span>
        <span className="user-menu-name">{user.displayName}</span>
        <span className="role-badge">{roleLabel(user.role)}</span>
        <span className="user-menu-caret" aria-hidden>
          ▾
        </span>
      </button>
      {open && (
        <div className="user-menu-list" id="user-menu-list">
          <div className="user-menu-who">
            <strong>{user.displayName}</strong>
            <span className="muted">{user.upn}</span>
            {viewingAs !== null && <span className="muted">Viewing as this person. Signing out signs out your own account ({viewingAs || 'you'}).</span>}
          </div>
          <Link className="user-menu-item" to="/account" onClick={() => close()}>
            My account
            <span className="muted"> — your access, time zone, this device</span>
          </Link>
          {user.role !== 'StoreTeam' && (
            <Link className="user-menu-item" to="/notifications" onClick={() => close()}>
              My notifications
              <span className="muted"> — emails when audits are completed</span>
            </Link>
          )}
          <ActionButton
            className="user-menu-item"
            busyLabel="Signing out…"
            onAction={async () => {
              setSigningOut(true);
              try {
                await onSignOut();
              } finally {
                setSigningOut(false); // cancelled at a prompt: the menu works again
              }
            }}
          >
            Sign out
          </ActionButton>
        </div>
      )}
    </div>
  );
}

/**
 * Sign out — on a shared device (store PC), first deal with this account's
 * audits saved here (owner ruling 2026-10-18): unsent changes stay (they
 * sync at this account's next sign-in here — nobody else's sign-in sends
 * them); anything already sent can be removed, photos included.
 */
async function signOut(signedInUpn: string, accountName: string | undefined, logout: () => Promise<unknown>): Promise<void> {
  const { unsent, clearable } = await myDeviceAudits(signedInUpn).catch(() => ({
    unsent: 0,
    clearable: [] as Awaited<ReturnType<typeof myDeviceAudits>>['clearable'],
  }));
  if (
    unsent > 0 &&
    !window.confirm(
      `${unsent} change${unsent === 1 ? '' : 's'} in your audits ha${unsent === 1 ? 's' : 've'}n’t synced yet. ` +
        'They stay saved on this device and sync the next time you sign in here. Sign out anyway?',
    )
  ) {
    return;
  }
  // Task comments/files saved with no signal (B4): same rule — waiting ones
  // stay for this account's next sign-in here; ones that couldn't be sent
  // can be removed.
  const outbox = getTaskOutbox();
  const queued = await outbox.counts().catch(() => null);
  if (
    queued &&
    queued.waiting > 0 &&
    !window.confirm(
      `${queued.waiting} task comment${queued.waiting === 1 ? '' : 's'}/file${queued.waiting === 1 ? '' : 's'} ha${queued.waiting === 1 ? 's' : 've'}n’t been sent yet. ` +
        'They stay saved on this device and send the next time you sign in here (within 72 hours of being written). Sign out anyway?',
    )
  ) {
    return;
  }
  if (
    queued &&
    queued.failed > 0 &&
    window.confirm(
      `Remove the ${queued.failed} task comment${queued.failed === 1 ? '' : 's'}/file${queued.failed === 1 ? '' : 's'} that couldn’t be sent from this device? ` +
        'Recommended on a shared computer.',
    )
  ) {
    await outbox.discardFailed().catch(() => undefined);
  }
  if (
    clearable.length > 0 &&
    window.confirm(
      `Remove your ${clearable.length} saved audit${clearable.length === 1 ? '' : 's'} (and photos) from this device? ` +
        'Everything in them is already saved online. Recommended on a shared computer.',
    )
  ) {
    await removeFromDevice(clearable).catch(() => undefined);
  }
  try {
    // A "your name" left in this tab by an app version before 2026-10-26
    // (nothing writes it any more — owner ruling: never remember a typed name).
    sessionStorage.removeItem('auditplus.commenterName');
  } catch {
    // storage blocked: nothing remembered
  }
  // The profile copy that opens the app quickly (auth/profileCache.ts).
  stopSavingProfiles();
  forgetCachedProfile(signedInUpn);
  if (accountName) forgetCachedProfile(accountName);
  // Lists read in the last minute (memory only) — gone even if the redirect is cancelled.
  clearReadCache();
  await logout();
}

/** Unsent audit changes + waiting task items, this account's and others' (null = couldn't read). */
async function deviceSplit(): Promise<{ mine: number; others: number } | null> {
  const [audits, tasks] = await Promise.all([getSyncEngine().pendingSplit(), getTaskOutbox().pendingSplit()]);
  if (audits === null || tasks === null) return null;
  return { mine: audits.mine + tasks.mine, others: audits.others + tasks.others };
}

/**
 * The old address, and this device may still hold unsynced audit work: the
 * move to the new address waits (src/canonicalHost.ts) until the sync
 * engine has sent everything. The button re-checks before it moves.
 */
function MovedBanner() {
  const move = getPendingMove();
  const status = useSyncStatus();
  // Task comments/files waiting on this device (B4) hold the move too.
  const tasksWaiting = useTaskOutbox().items.filter((i) => i.state === 'waiting').length;
  const [note, setNote] = useState<string | null>(null);
  // Shared devices: another account's unsynced audit can't sync under this
  // sign-in, so it must not hold this person at the old address forever.
  const [others, setOthers] = useState(0);
  useEffect(() => {
    if (!move) return;
    let live = true;
    void deviceSplit().then((split) => {
      if (live) setOthers(split?.others ?? 0);
    });
    return () => {
      live = false;
    };
  }, [move, status, tasksWaiting]);
  if (!move) return null;
  const host = new URL(move.target).host;
  const synced = status.state === 'synced' && status.pendingCount === 0 && tasksWaiting === 0;
  const go = async () => {
    const split = await deviceSplit();
    if (split === null) {
      if (move.unknown) window.location.replace(move.target);
      else setNote('Couldn’t check this device’s saved audits — try again in a moment.');
      return;
    }
    if (split.mine > 0) {
      setNote('Some of your saved work (audits or task comments) still hasn’t synced — stay online a little longer.');
      return;
    }
    if (
      split.others > 0 &&
      !window.confirm(
        'Another account has audit changes saved on this device that haven’t synced. They stay here, at the old address, and send when that person signs in here again.\n\nGo to the new address anyway?',
      )
    ) {
      return;
    }
    window.location.replace(move.target);
  };
  return (
    <div className="moved-banner" role="status">
      AuditPlus has a new address: <strong>{host}</strong>.{' '}
      {move.unknown
        ? 'This device’s saved audit work couldn’t be checked. If you have an audit in progress here, open it and let it sync before you move.'
        : !synced
          ? 'This device still has audit changes that haven’t synced — stay online and keep this page open until they do.'
          : others > 0
            ? 'Your audit work has synced. Another account has unsynced audit work on this device — it sends when that person signs in here.'
            : 'Everything on this device has synced.'}
      {note && <span> {note}</span>}
      <ActionButton
        className="btn btn-small"
        disabled={!move.unknown && !synced}
        busyLabel="Checking…"
        onAction={go}
      >
        Go to the new address
      </ActionButton>
    </div>
  );
}
